import { useState, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Plus, Pencil, Trash2, HelpCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import ArticleEdit from '@/components/article/article-edit';
import { Content } from '@/types/custom';
import { useContentList } from '@/lib/hooks/use-content';
import Loading from '@/components/loading';
import { searchContentList } from '@/lib/search';
import { normalizeImageUrl } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';
import { useAdminPreview } from '@/lib/admin-preview-context';

export default function AdminTips() {
  const { isPreviewMode } = useAdminPreview();
  const roleScope = isPreviewMode ? 'hoofdhartuser' : null;
  const [editingArticle, setEditingArticle] = useState<Content | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [refreshCount, setRefreshCount] = useState<number>(0);
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');

  const { contentWithConfigs: allArticles, loading } = useContentList('article', [
    refreshCount,
  ]);

  // Filter articles by role_scope (include 'both' in either view)
  const articles = useMemo(() => {
    return allArticles.filter((a) => {
      const scope = (a.content as any).role_scope ?? null;
      if (scope === 'both') return true;
      return scope === roleScope;
    });
  }, [allArticles, roleScope]);

  const sortedArticles = useMemo(() => {
    return [...articles].sort((a, b) => {
      const aDate = a.content.updated_at ?? a.content.created_at;
      const bDate = b.content.updated_at ?? b.content.created_at;
      return new Date(bDate).getTime() - new Date(aDate).getTime();
    });
  }, [articles]);

  const filteredArticles = useMemo(() => {
    if (searchTerm.trim() === '') {
      return sortedArticles;
    }
    const results = searchContentList(
      searchTerm,
      sortedArticles.map((x) => x.content),
    );

    return sortedArticles.filter((item, i) => results[i]);
  }, [searchTerm, sortedArticles]);

  const handleTogglePublish = async (article: Content) => {
    try {
      const { error } = await supabase
        .from('content')
        .update({ published: !article.published })
        .eq('id', article.id);
      if (error) throw error;
      toast({
        title: article.published ? 'Artikel verborgen' : 'Artikel gepubliceerd',
        description: `${article.title} is nu ${article.published ? 'verborgen' : 'zichtbaar'} voor gebruikers.`,
      });
      setRefreshCount((prev) => prev + 1);
    } catch (err: any) {
      toast({
        title: 'Fout bij wijzigen publicatiestatus',
        description: err.message,
        variant: 'destructive',
      });
    }
  };

  const handleDeleteArticle = async (article: Content) => {
    if (!confirm('Weet je zeker dat je dit artikel wilt verwijderen?')) return;

    try {
      await supabase.from('content').delete().eq('id', article.id);
      toast({
        title: 'Artikel verwijderd',
        description: `${article.title} is verwijderd.`,
      });
      setRefreshCount((prev) => prev + 1);
    } catch (err: any) {
      toast({
        title: 'Fout bij verwijderen artikel',
        description: err.message,
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="min-h-screen p-8 space-y-8">
      <div className="flex items-center">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-semibold">{isPreviewMode ? 'HnH Tips & Artikelen' : 'Tips & Artikelen'}</h1>
        </div>
        <Button
          className="bg-green-600 hover:bg-green-700 ml-6"
          onClick={() => {
            setEditingArticle(null);
            setDialogOpen(true);
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Artikel Toevoegen
        </Button>
      </div>

      <div className="mb-4 relative">
        <div className="relative w-[30%]">
          <Input
            className="pl-10 w-full"
            placeholder="Zoek artikelen op titel of beschrijving..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredArticles.map((item) => (
          <Card
            key={item.content.id}
            className="overflow-hidden hover:shadow-lg transition-shadow"
          >
            <div className="relative aspect-video">
              <img
                src={normalizeImageUrl(item.content.image_url)}
                alt={item.content.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-black/50 hover:bg-black/70 text-white border-white/20"
                  onClick={() => {
                    setEditingArticle(item.content);
                    setDialogOpen(true);
                  }}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  className="bg-black/50 hover:bg-red-700/70 text-white border-white/20"
                  onClick={() => handleDeleteArticle(item.content)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="absolute top-4 right-4 flex items-center gap-2 bg-black/50 rounded-lg px-3 py-1.5">
                <span className="text-xs text-white/80">
                  {item.content.published ? 'Gepubliceerd' : 'Verborgen'}
                </span>
                <Switch
                  checked={item.content.published}
                  onCheckedChange={() => handleTogglePublish(item.content)}
                  className="data-[state=checked]:bg-green-500"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent text-white">
                <h3 className="font-semibold text-lg">{item.content.title}</h3>
                <p className="text-sm text-white/80 mt-1">
                  {item.content.description}
                </p>
                {item.content.body && (
                  <div className="mt-2 pt-2 border-t border-white/20">
                    <div className="text-xs text-white/70 line-clamp-2 rich-text-preview">
                      <div
                        dangerouslySetInnerHTML={{ __html: item.content.body }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {filteredArticles.length === 0 && !loading && (
        <div className="text-center py-8 text-muted-foreground">
          {searchTerm
            ? `Geen artikelen gevonden voor "${searchTerm}"`
            : 'Geen artikelen beschikbaar.'}
        </div>
      )}

      {/* Article Edit Dialog */}
      <ArticleEdit
        content={editingArticle ?? undefined}
        onSave={() => setRefreshCount((prev) => prev + 1)}
        isOpen={dialogOpen}
        handleClose={() => {
          setDialogOpen(false);
          setEditingArticle(null);
        }}
      />
    </div>
  );
}
